import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Sparkles, Shield, MapPin, Play, Pause } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import Navbar from "@/components/Navbar";
import ListingCard from "@/components/ListingCard";
import Testimonials from "@/components/Testimonials";
import FAQSection from "@/components/FAQSection";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";
import { OrganizationSchema, WebsiteSchema, FAQSchema } from "@/components/seo/StructuredData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const [videoPaused, setVideoPaused] = useState(false);
  // Scroll-driven cross-fade Hero -> Video (Apple.com sticky pattern)
  const [scrollY, setScrollY] = useState(0);
  const [vh, setVh] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 800));

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/listings`, { signal: controller.signal });
        setListings(res.data.listings || []);
      } catch {
        if (!controller.signal.aborted) {
          setListings([]);
          setLoadError(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [requestVersion]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/unterkuenfte${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ""}`);
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>{`Oona Flats | Apartments in Wien online buchen`}</title>
        <meta
          name="description"
          content="Oona Flats: Premium-Apartments in Wien handverlesen, im Apple-Style. Direkt online buchen, sichere Stripe-Zahlung, sofortige Bestätigung. Ab 90 € pro Nacht."
        />
        <meta name="keywords" content="Apartment Wien, Ferienwohnung Wien buchen, Premium Apartments Vienna, Oona Flats, SharedSpace, Apartments mit Stil" />
        <meta property="og:title" content="Oona Flats | Premium-Apartments in Wien online buchen" />
        <meta property="og:description" content="Handverlesene Apartments in Wien im Apple-Style. Direkt online buchen, sichere Zahlung, sofortige Bestätigung." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1776893471287-5a88c2e0743c?w=1200&h=630&fit=crop" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://oonaflats.com/" />
        <meta property="og:locale" content="de_AT" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Oona Flats | Premium-Apartments in Wien" />
        <meta name="twitter:description" content="Handverlesene Apartments in Wien im Apple-Style. Direkt online buchen." />
        <link rel="canonical" href="https://oonaflats.com/" />
        <link rel="alternate" hrefLang="de" href="https://oonaflats.com/" />
        <link rel="alternate" hrefLang="en" href="https://oonaflats.com/" />
      </Helmet>
      <OrganizationSchema />
      <WebsiteSchema />
      <FAQSchema />
      <Navbar />

      {/* Sticky Hero → Video Cross-Fade (Apple.com Style)
          200vh outer (150vh on mobile) gives scroll-distance; sticky inner pins both layers
          while scrollY-driven opacity cross-fades them.
          No snap-section here — the cross-fade needs every intermediate scroll position. */}
      <section data-testid="hero-section" className="relative h-[150vh] md:h-[200vh]">
        <div className="sticky top-0 h-screen min-h-[600px] w-full overflow-hidden">
          {/* Compute fade progress 0→1 over the first viewport-height of scroll */}
          {(() => {
            // Cross-fade completes within ~0.75 viewport-height of scroll (works for both 150vh mobile + 200vh desktop wrappers)
            const progress = Math.min(scrollY / (vh * 0.75), 1);
            const heroOpacity = Math.max(0, 1 - progress * 1.4);  // fade out first
            const videoOpacity = Math.max(0, progress * 1.6 - 0.1); // fade in slightly later
            const heroTextOpacity = Math.max(0, 1 - progress * 2.2);
            const videoTextOpacity = Math.max(0, progress * 2.4 - 0.8);

            return (
              <>
                {/* HERO LAYER */}
                <div
                  className="absolute inset-0"
                  style={{
                    opacity: heroOpacity,
                    pointerEvents: heroOpacity > 0.3 ? "auto" : "none",
                    willChange: "opacity",
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_hostaway-connect/artifacts/wmb6kgzt_hbieser-castle-2003867.jpg"
                    alt="Schloss Belvedere Wien"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      transform: `scale(${1 + progress * 0.18})`,
                      willChange: "transform",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/50 to-white/20" />
                  <div
                    className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center"
                    style={{
                      opacity: heroTextOpacity,
                      transform: `translate3d(0, ${progress * 60}px, 0)`,
                      willChange: "opacity, transform",
                    }}
                  >
                    <h1 data-testid="hero-title" className="text-5xl md:text-7xl font-semibold tracking-tighter leading-tight text-[#1D1D1F] mb-6 max-w-4xl animate-fade-in-up" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {t("hero_title")}
                    </h1>
                    <p className="text-lg md:text-xl text-[#1D1D1F]/70 mb-10 max-w-2xl animate-fade-in-up animate-delay-100">
                      {t("hero_subtitle")}
                    </p>
                    <form onSubmit={handleSearch} data-testid="hero-search-form" className="w-full max-w-2xl animate-fade-in-up animate-delay-200">
                      <div className="crystal-glass-strong search-pill-shadow rounded-full flex items-center p-2">
                        <div className="flex-1 flex items-center gap-3 px-5">
                          <Search className="w-5 h-5 text-[#86868B] shrink-0" />
                          <input data-testid="hero-search-input" type="text" placeholder={t("hero_search_placeholder")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-transparent border-none focus:ring-0 focus:outline-none text-[#1D1D1F] placeholder:text-[#86868B] font-medium text-base" />
                        </div>
                        <button data-testid="hero-search-button" type="submit" className="bg-black text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1D1D1F] flex items-center gap-2 btn-press">
                          <span className="hidden sm:inline">{t("hero_search_button")}</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </form>
                    {/* Apple-style scroll hint */}
                    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#1D1D1F]/40 animate-fade-in-up animate-delay-300" aria-hidden="true">
                      <span className="text-xs font-medium tracking-[0.15em] uppercase">Scrollen</span>
                      <div className="w-5 h-8 rounded-full border border-[#1D1D1F]/30 flex items-start justify-center pt-2">
                        <span className="block w-1 h-2 rounded-full bg-[#1D1D1F]/50 scroll-hint-dot" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* VIDEO LAYER */}
                <div
                  ref={videoSectionRef}
                  data-testid="video-showcase-section"
                  className="absolute inset-0 bg-black"
                  style={{
                    opacity: videoOpacity,
                    pointerEvents: videoOpacity > 0.3 ? "auto" : "none",
                    willChange: "opacity",
                  }}
                >
                  <video ref={videoRef} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
                    <source src="https://customer-assets.emergentagent.com/job_hostaway-connect/artifacts/imm4eho2_seedance-2.0_luxeri%C3%B6ses_Apartment_zur_goldenen_Abendstunden_aufnahem_mit_Drohen_durch_das_Ap-0.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 lg:p-24">
                    <div
                      className="max-w-2xl"
                      style={{
                        opacity: videoTextOpacity,
                        transform: `translate3d(0, ${(1 - Math.min(videoTextOpacity, 1)) * 30}px, 0)`,
                        willChange: "opacity, transform",
                      }}
                    >
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60 mb-3">{t("video_label")}</p>
                      <h2 className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-white mb-4 leading-[0.95]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {t("video_title")}
                      </h2>
                      <p className="text-base md:text-lg text-white/70 max-w-lg mb-8 leading-relaxed">
                        {t("video_description")}
                      </p>
                      <button data-testid="video-cta-button" onClick={() => navigate("/unterkuenfte")}
                        className="inline-flex items-center gap-3 bg-white text-black rounded-full px-8 py-4 font-medium text-sm transition-all hover:scale-[1.02] active:scale-[0.98] btn-press">
                        {t("video_cta")} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                    <button data-testid="video-play-pause" onClick={() => { if (videoRef.current) { videoPaused ? videoRef.current.play() : videoRef.current.pause(); setVideoPaused(!videoPaused); } }}
                      className="absolute bottom-8 right-8 md:bottom-16 md:right-16 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                      style={{ opacity: videoOpacity }}>
                      {videoPaused ? <Play className="w-5 h-5 ml-0.5" /> : <Pause className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Features — full viewport on desktop, natural height on mobile */}
      <section className="snap-section md:min-h-screen flex items-center px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="max-w-7xl mx-auto w-full">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#86868B] mb-3">{t("listings_label")}</p>
              <h2 className="text-4xl md:text-6xl font-semibold tracking-tighter text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Wohnen mit Stil.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Reveal delay={0} y={50}>
              <div className="bg-white rounded-3xl border border-black/5 p-10 card-hover h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mb-6"><Sparkles className="w-7 h-7 text-[#1D1D1F]" /></div>
                <h3 className="text-2xl font-semibold tracking-tight text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("feature_style_title")}</h3>
                <p className="text-base text-[#86868B] leading-relaxed">{t("feature_style_desc")}</p>
              </div>
            </Reveal>
            <Reveal delay={140} y={50}>
              <div className="bg-white rounded-3xl border border-black/5 p-10 card-hover h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mb-6"><MapPin className="w-7 h-7 text-[#1D1D1F]" /></div>
                <h3 className="text-2xl font-semibold tracking-tight text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("feature_location_title")}</h3>
                <p className="text-base text-[#86868B] leading-relaxed">{t("feature_location_desc")}</p>
              </div>
            </Reveal>
            <Reveal delay={280} y={50}>
              <div className="bg-white rounded-3xl border border-black/5 p-10 card-hover h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mb-6"><Shield className="w-7 h-7 text-[#1D1D1F]" /></div>
                <h3 className="text-2xl font-semibold tracking-tight text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("feature_secure_title")}</h3>
                <p className="text-base text-[#86868B] leading-relaxed">{t("feature_secure_desc")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured Listings — full viewport on desktop, natural height on mobile */}
      <section data-testid="featured-listings" className="snap-section md:min-h-screen flex items-center px-6 md:px-12 lg:px-24 py-20 md:py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto w-full">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#86868B] mb-2">{t("listings_label")}</p>
                <h2 className="text-4xl md:text-6xl font-semibold tracking-tighter text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("listings_title")}</h2>
              </div>
              <button data-testid="view-all-listings-button" onClick={() => navigate("/unterkuenfte")}
                className="hidden md:flex items-center gap-2 text-sm font-medium text-[#1D1D1F] hover:text-[#86868B] transition-colors">
                {t("listings_view_all")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
          {loadError ? (
            <div data-testid="featured-listings-load-error" role="alert" className="py-16 text-center">
              <p className="text-lg font-medium text-[#1D1D1F]">{t("listings_load_error")}</p>
              <button
                data-testid="featured-listings-retry-button"
                onClick={() => setRequestVersion((version) => version + 1)}
                className="mt-5 rounded-full bg-black px-6 py-3 text-sm font-medium text-white btn-press"
              >
                {t("listings_retry")}
              </button>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (<div key={i} className="rounded-3xl overflow-hidden bg-white border border-black/5"><div className="aspect-[4/3] img-skeleton" /><div className="p-5 space-y-3"><div className="h-5 w-3/4 img-skeleton rounded" /><div className="h-4 w-1/2 img-skeleton rounded" /></div></div>))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {listings.slice(0, 6).map((listing, i) => (
                <Reveal key={listing.id} delay={(i % 3) * 100} y={50}>
                  <ListingCard listing={listing} index={i} />
                </Reveal>
              ))}
            </div>
          )}
          <div className="md:hidden mt-8 text-center">
            <button data-testid="view-all-listings-mobile" onClick={() => navigate("/unterkuenfte")} className="bg-black text-white rounded-full px-8 py-3 font-medium text-sm btn-press">
              {t("listings_view_all_mobile")}
            </button>
          </div>
        </div>
      </section>

      <Testimonials />
      <FAQSection />

      <Footer />
    </div>
  );
}
